<script setup lang="ts">
import { PV } from "@/widgets";
import { type Component, ref, watchEffect } from "vue";
import IconBlank from "~icons/icon-park-outline/lightning";
import IconSolid from "~icons/icon-park-solid/lightning";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormExt, FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = number;
interface PropsType extends BaseProps<ModelType> {
    max?: number;
    blank?: Component;
    solid?: Component;
    hasValue?: () => boolean;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    max: 10,
    blank: IconBlank,
    solid: IconSolid,
});

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: props.hasValue ?? (() => model.value !== undefined),
});

const actions = defineActions(model, props, state);
const hoverValue = ref(0);

watchEffect(() => {
    if (model.value === null) {
        model.value = undefined;
    }
});
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <PV.IconField>
                <div class="rns-input-box rns-input-box-inactive overflow-hidden p-0! pr-9!">
                    <PV.Rating
                        v-model="model"
                        class="overflow-x-auto p-3!"
                        disabled
                        :dt="{ gap: '20px' }"
                        fluid
                        :stars="props.max"
                    >
                        <template #onicon>
                            <Component :is="props.solid" class="text-black" />
                        </template>
                        <template #officon>
                            <Component :is="props.blank" class="text-black" />
                        </template>
                    </PV.Rating>
                </div>
                <FormExt :actions="actions" :state="state" />
            </PV.IconField>
        </template>
        <template v-else>
            <PV.IconField>
                <div
                    class="rns-input-box rns-input-box-active overflow-hidden p-0! pr-9!"
                    :class="{ 'rns-invalid': state.hasError }"
                    @mouseout="hoverValue = 0"
                >
                    <PV.Rating
                        v-model="model"
                        class="overflow-x-auto p-3!"
                        :dt="{ gap: '20px' }"
                        fluid
                        :stars="props.max"
                    >
                        <template #onicon="{ value }">
                            <Component
                                :is="props.solid"
                                :class="{
                                    'text-gray-500': value <= hoverValue,
                                    'text-gray-800': value > hoverValue,
                                }"
                                @mouseover="hoverValue = value"
                            />
                        </template>
                        <template #officon="{ value }">
                            <template v-if="value <= hoverValue">
                                <Component :is="props.solid" class="text-gray-500" @mouseover="hoverValue = value" />
                            </template>
                            <template v-else>
                                <Component :is="props.blank" class="text-gray-800" @mouseover="hoverValue = value" />
                            </template>
                        </template>
                    </PV.Rating>
                </div>
                <Buttons.ExtReset v-if="state.clearable && state.hasValue" @click="actions.onReset" />
            </PV.IconField>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-input-box {
    @apply flex flex-col rounded-md pl-1 p-2.5;
}

.rns-input-box-active {
    @apply shadow-xs border border-gray-300 hover:border-gray-400 pl-2.5 py-2.75;
}

.rns-input-box-inactive {
    @apply py-2.75;
    @apply rns-g-static-input;
}

.rns-invalid {
    @apply border-red-400;
}
</style>
